import { LocalStorageManager } from './localStorage';
import { addTask as addTaskToFirebase } from '../services/taskService';
import { addAppointment as addAppointmentToFirebase } from '../services/appointmentService';
import { addJob as addJobToFirebase } from '../services/jobService';
import { addCandidate as addCandidateToFirebase } from '../services/candidateService';

export interface RecycleBinItem {
  id: string;
  type: 'candidate' | 'task' | 'appointment' | 'document' | 'cv' | 'draft' | 'job' | 'user' | 'receptionist' | 'dashboard';
  name: string;
  details: string;
  deletedAt: string;
  deletedBy: string;
  data: any;
}

export const addToRecycleBin = (item: Omit<RecycleBinItem, 'id' | 'deletedAt'>) => {
  const recycleBin = JSON.parse(localStorage.getItem('recycleBin') || '[]');
  
  // Optimize data storage for large objects like CV documents
  let optimizedData = item.data;
  
  // For document type items, check if they contain large CV data
  if (item.type === 'document' && item.data) {
    // Check if this is a CV document with large data
    if (item.data.category === 'cv-drafts' || item.data.category === 'cv-builder-cvs') {
      // Store only essential data for CV documents to avoid quota issues
      optimizedData = {
        id: item.data.id,
        name: item.data.name,
        type: item.data.type,
        size: item.data.size,
        uploadedAt: item.data.uploadedAt,
        uploadedBy: item.data.uploadedBy,
        url: item.data.url,
        category: item.data.category,
        description: item.data.description,
        // Store original data reference for restoration
        isOptimized: true,
        originalId: item.data.id
      };
    }
  }
  
  const newItem: RecycleBinItem = {
    ...item,
    data: optimizedData,
    id: `${item.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    deletedAt: new Date().toISOString()
  };
  
  try {
    recycleBin.push(newItem);
    localStorage.setItem('recycleBin', JSON.stringify(recycleBin));
    
    // Trigger storage event for same-tab updates
    window.dispatchEvent(new Event('storage'));
  } catch (error: any) {
    if (error.name === 'QuotaExceededError') {
      console.warn('⚠️ LocalStorage quota exceeded. Attempting cleanup and retry...');
      
      // Try to clean up old recycle bin items
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const cleanedBin = recycleBin.filter((recycleItem: RecycleBinItem) => 
        new Date(recycleItem.deletedAt) > thirtyDaysAgo
      );
      
      try {
        cleanedBin.push(newItem);
        localStorage.setItem('recycleBin', JSON.stringify(cleanedBin));
        window.dispatchEvent(new Event('storage'));
        console.log('✅ Successfully added to recycle bin after cleanup');
      } catch (retryError) {
        console.error('❌ Still unable to add to recycle bin after cleanup:', retryError);
        alert('Unable to move document to recycle bin due to storage limitations. The document will be permanently deleted instead.');
        throw retryError;
      }
    } else {
      throw error;
    }
  }
};

export const getRecycleBinItems = (): RecycleBinItem[] => {
  return JSON.parse(localStorage.getItem('recycleBin') || '[]');
};

export const restoreFromRecycleBin = async (itemId: string) => {
  const recycleBin = getRecycleBinItems();
  const item = recycleBin.find(i => i.id === itemId);
  
  if (!item) return false;
  
  // Remove from recycle bin first
  const updatedBin = recycleBin.filter(i => i.id !== itemId);
  localStorage.setItem('recycleBin', JSON.stringify(updatedBin));
  
  // Restore to appropriate storage based on type using Firebase
  try {
    switch (item.type) {
      case 'candidate':
        // Create a new candidate object without the ID to avoid conflicts
        const candidateData = { ...item.data };
        delete candidateData.id;
        delete candidateData._id;
        delete candidateData.createdAt;
        delete candidateData.updatedAt;
        
        const newCandidateId = await addCandidateToFirebase(candidateData);
        console.log('✅ Restored candidate to Firebase:', candidateData.name, 'with ID:', newCandidateId);
        break;
        
      case 'task':
        // Create a new task object without the ID to avoid conflicts
        const taskData = { ...item.data };
        delete taskData.id;
        delete taskData._id;
        delete taskData.createdAt;
        delete taskData.updatedAt;
        
        const newTaskId = await addTaskToFirebase(taskData);
        console.log('✅ Restored task to Firebase:', taskData.title, 'with ID:', newTaskId);
        break;
        
      case 'appointment':
        // Create a new appointment object without the ID to avoid conflicts
        const appointmentData = { ...item.data };
        delete appointmentData.id;
        delete appointmentData._id;
        delete appointmentData.createdAt;
        delete appointmentData.updatedAt;
        
        const newAppointmentId = await addAppointmentToFirebase(appointmentData);
        console.log('✅ Restored appointment to Firebase:', appointmentData.candidateName, 'with ID:', newAppointmentId);
        break;
    case 'document':
      // For documents, restore to uploadedDocuments
      if (item.data.isOptimized && (item.data.category === 'cv-drafts' || item.data.category === 'cv-builder-cvs')) {
        // For optimized CV documents, we can only restore the metadata
        // The actual CV data may have been permanently lost to save space
        console.warn('⚠️ Restoring optimized CV document. Full data may not be available.');
        const documents = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
        documents.push(item.data);
        localStorage.setItem('uploadedDocuments', JSON.stringify(documents));
        console.log('✅ Restored optimized CV document:', item.data.name);
      } else {
        // Regular document restoration
        const documents = JSON.parse(localStorage.getItem('uploadedDocuments') || '[]');
        documents.push(item.data);
        localStorage.setItem('uploadedDocuments', JSON.stringify(documents));
        console.log('✅ Restored document:', item.data.name);
      }
      break;
    case 'cv':
      const cvs = JSON.parse(localStorage.getItem('generatedCVs') || '[]');
      cvs.push(item.data);
      localStorage.setItem('generatedCVs', JSON.stringify(cvs));
      console.log('✅ Restored CV:', item.data.name);
      break;
    case 'draft':
      // Use LocalStorageManager for CV drafts
      try {
        LocalStorageManager.addCVDraft(item.data);
        console.log('✅ Restored CV draft via LocalStorageManager:', item.data.name);
      } catch (error) {
        console.error('❌ Failed to restore CV draft:', error);
        const drafts = JSON.parse(localStorage.getItem('recruitment_crm_cv_drafts') || '[]');
        drafts.push(item.data);
        localStorage.setItem('recruitment_crm_cv_drafts', JSON.stringify(drafts));
      }
      break;
      case 'job':
        // Create a new job object without the ID to avoid conflicts
        const jobData = { ...item.data };
        delete jobData.id;
        delete jobData.createdAt;
        delete jobData.updatedAt;
        
        const newJobId = await addJobToFirebase(jobData);
        console.log('✅ Restored job to Firebase:', jobData.title, 'with ID:', newJobId);
        break;
      case 'user':
        // For users, restore to recruitment-users
        const users = JSON.parse(localStorage.getItem('recruitment-users') || '[]');
        users.push(item.data);
        localStorage.setItem('recruitment-users', JSON.stringify(users));
        // Trigger global event for user updates
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'recruitment-users',
          newValue: JSON.stringify(users)
        }));
        console.log('✅ Restored user:', item.data.name);
        break;
      case 'receptionist':
        const receptionistItems = JSON.parse(localStorage.getItem('receptionistItems') || '[]');
        receptionistItems.push(item.data);
        localStorage.setItem('receptionistItems', JSON.stringify(receptionistItems));
        console.log('✅ Restored receptionist item:', item.name);
        break;
      case 'dashboard':
        const dashboardItems = JSON.parse(localStorage.getItem('dashboardItems') || '[]');
        dashboardItems.push(item.data);
        localStorage.setItem('dashboardItems', JSON.stringify(dashboardItems));
        console.log('✅ Restored dashboard item:', item.name);
        break;
    }
    
    // Trigger storage event for same-tab updates
    window.dispatchEvent(new Event('storage'));
    
    return true;
  } catch (error) {
    console.error('❌ Failed to restore item from recycle bin:', error);
    // Re-add to recycle bin if restoration failed
    const recycleBin = getRecycleBinItems();
    recycleBin.push(item);
    localStorage.setItem('recycleBin', JSON.stringify(recycleBin));
    throw error;
  }
};

export const permanentlyDelete = (itemId: string) => {
  const recycleBin = getRecycleBinItems();
  const updatedBin = recycleBin.filter(i => i.id !== itemId);
  localStorage.setItem('recycleBin', JSON.stringify(updatedBin));
  
  // Trigger storage event for same-tab updates
  window.dispatchEvent(new Event('storage'));
  
  return true;
};

export const emptyRecycleBin = () => {
  localStorage.setItem('recycleBin', JSON.stringify([]));
  
  // Trigger storage event for same-tab updates
  window.dispatchEvent(new Event('storage'));
  
  return true;
};

// Auto-cleanup function (removes items older than 30 days)
export const cleanupRecycleBin = () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recycleBin = getRecycleBinItems();
  const itemsToDelete = recycleBin.filter(item => 
    new Date(item.deletedAt) <= thirtyDaysAgo
  );
  const itemsToKeep = recycleBin.filter(item => 
    new Date(item.deletedAt) > thirtyDaysAgo
  );
  
  if (itemsToKeep.length < recycleBin.length) {
    console.log(`🧹 Auto-cleanup: Removing ${itemsToDelete.length} items older than 30 days`);
    itemsToDelete.forEach(item => {
      console.log(`  - ${item.name} (deleted on ${new Date(item.deletedAt).toLocaleDateString()})`);
    });
    localStorage.setItem('recycleBin', JSON.stringify(itemsToKeep));
    window.dispatchEvent(new Event('storage'));
    return itemsToDelete.length;
  }
  return 0;
};